<template>
  <b-container>
    <b-row>
      <b-col>
        <div class="form form-sm my-4 my-lg-5 mx-auto">
          <form-header v-if="!showSetPassword" bookmarkable>
            <template #sub-heading>
              <span class="d-block mb-0 small"
                >Enter your email address to sign in.</span
              >
            </template>
          </form-header>

          <SsoSetPassword
            v-if="showSetPassword"
            :email="formData.email"
            :sso-login-url="ssoLoginUrl"
          />

          <b-alert
            v-if="showFirstTimePrompt"
            variant="primary"
            show
            class="text-center mb-4"
          >
            <p class="mb-3">
              Due to the planned implementation of SSO capabilities and enhanced
              security protocols, all users are required to change their
              password. Please select <strong>NEXT</strong> to reset your
              password and login.
            </p>
            <base-button type="primary" size="lg" block @click="continueToSso">
              Next
            </base-button>
          </b-alert>

          <validation-observer
            v-if="!showFirstTimePrompt && !showSetPassword"
            v-slot="{ invalid }"
            ref="formRef"
            class="form form-sm mx-auto"
          >
            <b-form @submit.prevent="handleSubmit">
              <base-input
                v-model="formData.email"
                rules="required|email"
                name="Email Address"
                label="Email Address"
              ></base-input>

              <TermlyCheckbox v-model="formData.terms" />

              <base-button
                :disabled="invalid || checkingSso"
                :loading="checkingSso"
                type="primary"
                native-type="submit"
                size="lg"
                class="mt-4"
                block
                >{{ checkingSso ? "Checking sign-in method..." : "Log In" }}</base-button
              >
            </b-form>
          </validation-observer>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import Vue from 'vue'
import { defineComponent, reactive, ref, onMounted } from '@vue/composition-api'
import { useRoute } from 'vue2-helpers/vue-router'

import { useSsoLogin } from '@composables'

import FormHeader from '@components/FormHeader'
import SsoSetPassword from '@components/SsoSetPassword'
import TermlyCheckbox from '@components/Termly/TermlyCheckbox'

export default defineComponent({
  title: 'Login',
  components: {
    FormHeader,
    SsoSetPassword,
    TermlyCheckbox
  },
  setup() {
    // Pre-fill from ?email=, which arrives two ways: straight from an SSO invite
    // link, or carried over when /login redirects here because passwords are off
    // for this environment.
    const route = useRoute()
    const prefillEmail = (route.query.email || '').trim()

    // Where to return to after login - e.g. Django admin sends an
    // unauthenticated /411/ visitor here with ?next=/backend/411/. Absent for
    // an ordinary login, which lands on the dashboard as usual.
    const next = (route.query.next || '').trim()

    // Initialise email at creation time so Vue 2 reactivity tracks it.
    const formData = reactive({ email: prefillEmail })

    const formRef = ref()

    // Sign-in routing lives in useSsoLogin, shared in case another screen
    // needs to start a login the same way in future.
    const {
      checkingSso,
      showFirstTimePrompt,
      showSetPassword,
      ssoLoginUrl,
      loadAuthConfig,
      resolveSsoForEmail,
      continueToSso
    } = useSsoLogin(next)

    // Show a message if the SSO round-trip bounced back with a failure.
    // The backend redirects failed logins to this page with
    // ?sso_error=1 and creates no session; here we surface a friendly message.
    onMounted(() => {
      loadAuthConfig()

      // An invited user arrives with their address already known, so resolve it
      // now and show them the first-time prompt (or the set-password screen)
      // rather than a form they would only retype their own email into.
      if (prefillEmail) resolveSsoForEmail(prefillEmail)

      const params = new URLSearchParams(window.location.search)
      if (params.get('sso_error')) {
        Vue.prototype.$toasted.show(
          'Single sign-on was unsuccessful. Please try again.',
          { type: 'error' }
        )
      }
    })

    const handleSubmit = async () => {
      // Only now - after the user has entered a valid email, accepted the
      // terms, and clicked Log In (the button stays disabled until all three) -
      // do we resolve the sign-in method. Every active account is on Entra SSO,
      // so anything this doesn't resolve is an unrecognized or mistyped email.
      const resolved = await resolveSsoForEmail(formData.email)
      if (!resolved) {
        Vue.prototype.$toasted.show(
          "We couldn't find an account with that email. Please check and try again.",
          { type: 'error' }
        )
      }
    }

    return {
      formData,
      formRef,
      checkingSso,
      showFirstTimePrompt,
      showSetPassword,
      // Bound by the SsoSetPassword above; without it that component received
      // undefined and lost its fallback link to the hosted Entra page.
      ssoLoginUrl,
      continueToSso,
      handleSubmit
    }
  }
})
</script>
